import { motion } from 'framer-motion';
import { CheckCircle, Circle, Clock } from 'lucide-react';

export default function ConstructionStageChart({ currentStage, stages }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8" data-testid="construction-stage-chart">
      <h3 className="text-xl font-medium mb-8">Construction Progress</h3>
      
      {/* Progress Bar */}
      <div className="relative mb-12">
        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#4ADE80] to-[#22c55e]"
            initial={{ width: 0 }}
            animate={{ width: `${currentStage.progress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
        <div className="absolute -top-8 left-0 right-0 flex justify-between px-1">
          <span className="text-xs text-white/40">Start</span>
          <span className="text-sm text-[#4ADE80] font-mono">{currentStage.progress}% Complete</span>
          <span className="text-xs text-white/40">Complete</span>
        </div>
      </div>

      {/* Stages */}
      <div className="space-y-6">
        {stages.map((stage, index) => {
          const isCompleted = stage.progress === 100;
          const isCurrent = stage.id === currentStage.id;
          const isPending = stage.progress === 0;
          
          return (
            <motion.div
              key={stage.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-start space-x-4 p-4 rounded-lg transition-colors duration-300 ${
                isCurrent ? 'bg-[#4ADE80]/5 border border-[#4ADE80]/20' : 'bg-transparent'
              }`}
              data-testid={`stage-${index}`}
            >
              <div className="flex-shrink-0 mt-1">
                {isCompleted ? (
                  <CheckCircle className="w-6 h-6 text-[#4ADE80]" />
                ) : isCurrent ? (
                  <div className="relative">
                    <Circle className="w-6 h-6 text-[#4ADE80] fill-[#4ADE80]/20" />
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <Clock className="w-4 h-4 text-[#4ADE80]" />
                    </motion.div>
                  </div>
                ) : (
                  <Circle className="w-6 h-6 text-white/20" />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className={`font-medium ${
                    isCurrent ? 'text-[#4ADE80]' : isCompleted ? 'text-white' : 'text-white/40'
                  }`}>
                    {stage.name}
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    isCompleted ? 'bg-[#4ADE80]/10 text-[#4ADE80]' :
                    isCurrent ? 'bg-[#4ADE80]/10 text-[#4ADE80]' :
                    'bg-white/5 text-white/40'
                  }`}>
                    {isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Pending'}
                  </span>
                </div>
                <p className="text-sm text-white/60 mb-2">{stage.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/40">{stage.timeline}</span>
                  {stage.valueIncrease && (
                    <span className="text-[#4ADE80] font-mono">+{stage.valueIncrease}% Value</span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}